/**
 * Issue #460 — sort orders for the Coach Portal's four unbounded lists.
 *
 * Martin: *"Goals, Feedback, Match reflections and Reflections will get
 * long over time, need to enable scrolling and potential sorting function
 * to make it easier to review for a coach."*
 *
 * The tables live here rather than inside the panels for the reason
 * `activityCreate.js` gives for its own: this repo mounts no components in
 * unit tests (#332), so a comparator written inline in JSX is a rule only
 * the e2e suite can check. Pure functions over plain rows are testable per
 * commit, and the panels below become "render what this gives me".
 *
 * ONE DEFAULT, EVERYWHERE: the first entry of every table is newest-first,
 * which is the order all four lists already arrived in before this change
 * (the API sorts them that way — see `db/repos/*.ts`). A sort control whose
 * default silently re-orders a list a coach already knows is a worse
 * starting point than no control at all.
 *
 * MISSING VALUES SORT LAST IN BOTH DIRECTIONS, on purpose and not by
 * accident of comparison. A goal with no due date is not "due first"; a
 * reflection with no opponent is not "opponent A". Letting an empty string
 * win an ascending sort puts the least informative rows at the top of the
 * one screen a coach is trying to review.
 */

/** Read `field` off a row, where `field` may itself be a derivation. */
const valueOf = (row, field) => (typeof field === "function" ? field(row) : row?.[field]);

/**
 * ISO-8601 strings compare lexicographically exactly as they compare
 * chronologically, which is why nothing here parses a Date. Both shapes on
 * the wire qualify: `created_at`/`created_date` are full timestamps and
 * `target_date`/`match_date` are `YYYY-MM-DD` (see `api/src/routes/`).
 */
function byDate(field, direction) {
  return (a, b) => {
    const av = valueOf(a, field) || "";
    const bv = valueOf(b, field) || "";
    if (!av || !bv) return (av ? 0 : 1) - (bv ? 0 : 1);
    return direction === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
  };
}

/** Case- and accent-insensitive, so "batting" and "Batting" do not split. */
function byText(field) {
  return (a, b) => {
    const av = (valueOf(a, field) ?? "").toString().trim();
    const bv = (valueOf(b, field) ?? "").toString().trim();
    if (!av || !bv) return (av ? 0 : 1) - (bv ? 0 : 1);
    return av.localeCompare(bv, undefined, { sensitivity: "base" });
  };
}

/** Absent/NaN counts as 0 — a goal with no recorded progress has made none. */
function byNumberDesc(field) {
  return (a, b) => {
    const av = Number(valueOf(a, field));
    const bv = Number(valueOf(b, field));
    return (Number.isFinite(bv) ? bv : 0) - (Number.isFinite(av) ? av : 0);
  };
}

/**
 * Pinned notes stay at the top of the Feedback list under EVERY order.
 *
 * Pinning predates this issue and is a coach's explicit "keep this in
 * front of me" (`togglePin` in CoachPortal.jsx). A sort that could bury a
 * pinned note would quietly revoke that, so pin is the primary key and the
 * chosen order is the tiebreak — which is also what the list did before,
 * where pinned-first was the only order there was.
 */
function pinnedFirst(compare) {
  return (a, b) => (b?.pinned ? 1 : 0) - (a?.pinned ? 1 : 0) || compare(a, b);
}

/** Goals — `GET /goals` (routes/goals.ts `toApiGoal`). */
export const GOAL_SORTS = [
  { value: "newest", label: "Newest first", compare: byDate("created_at", "desc") },
  { value: "oldest", label: "Oldest first", compare: byDate("created_at", "asc") },
  { value: "due", label: "Due soonest", compare: byDate("target_date", "asc") },
  { value: "progress", label: "Most progress", compare: byNumberDesc("progress_percentage") },
  { value: "title", label: "Title A–Z", compare: byText("goal_title") },
];

/** Coach feedback notes — `GET /notes` (routes/notes.ts). */
export const FEEDBACK_SORTS = [
  { value: "newest", label: "Newest first", compare: pinnedFirst(byDate("created_date", "desc")) },
  { value: "oldest", label: "Oldest first", compare: pinnedFirst(byDate("created_date", "asc")) },
  { value: "category", label: "Category A–Z", compare: pinnedFirst(byText((n) => n?.category || "general")) },
];

/** The player's own match reflections — `GET /reflections?kind=match`. */
export const MATCH_REFLECTION_SORTS = [
  { value: "newest", label: "Newest first", compare: byDate("created_at", "desc") },
  { value: "oldest", label: "Oldest first", compare: byDate("created_at", "asc") },
  { value: "match", label: "Match date", compare: byDate("match_date", "desc") },
  { value: "opponent", label: "Opponent A–Z", compare: byText("match_opponent") },
];

/** The coach's own private reflections — `GET /coach-reflections` (#205). */
export const REFLECTION_SORTS = [
  { value: "newest", label: "Newest first", compare: byDate("created_at", "desc") },
  { value: "oldest", label: "Oldest first", compare: byDate("created_at", "asc") },
  { value: "edited", label: "Recently edited", compare: byDate((r) => r?.updated_at || r?.created_at, "desc") },
];

/** The order a list starts in: the first entry, i.e. newest-first. */
export function defaultSort(sorts) {
  return sorts[0].value;
}

/**
 * A NEW array in the chosen order. Never sorts in place: `goals`, `notes`
 * and both reflection lists are React state, and `Array.prototype.sort`
 * mutates — re-ordering state under React is how a list stops re-rendering
 * when it changes.
 *
 * An unknown `value` falls back to the default rather than to the input
 * order, so a stale URL or a renamed option cannot leave a list in an order
 * the control claims it is not in.
 */
export function sortRows(sorts, value, rows) {
  const chosen = sorts.find((s) => s.value === value) || sorts[0];
  return [...(rows || [])].sort(chosen.compare);
}
